import styled, { css, keyframes } from 'styled-components';

// Animations
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(-10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

// Shared styles
const glassMorphism = css`
  background: ${(p) => p.theme.colors.surface}ee;
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
`;

const cardShadow = css`
  box-shadow:
    0 1px 3px rgba(0, 0, 0, 0.6),
    0 1px 2px rgba(0, 0, 0, 0.6);
`;

const cardShadowHover = css`
  box-shadow:
    0 3px 6px rgba(0, 0, 0, 0.15),
    0 2px 4px rgba(0, 0, 0, 0.12);
`;

// Separate component for Header Actions because they are rendered
// outside the main OrdersPageWrapper context (in the ContentContainer header)
export const HeaderSearchForm = styled.form`
  display: flex;
  align-items: center;
  width: clamp(8rem, 24vw, 16rem);
  height: 2.25rem;
  background: ${(p) => p.theme.colors.surface};
  color: ${(p) => p.theme.colors.onSurface};
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 0.5rem;
  overflow: hidden;

  &:focus-within {
    outline: 2px solid ${(p) => p.theme.colors.onPrimary};
    outline-offset: 2px;
  }

  input {
    flex: 1;
    min-width: 0;
    width: 100%;
    padding: 0 0.5rem;
    border: none;
    outline: none;
    background: transparent;
    color: inherit;
    font-size: 0.85rem;

    &::placeholder {
      color: ${(p) => p.theme.colors.textLight};
    }
  }

  button {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 1.75rem;
    height: 100%;
    padding: 0;
    border: none;
    background: transparent;
    color: inherit;
    cursor: pointer;

    &:hover, &:focus-visible {
      background: ${(p) => p.theme.colors.onSurface}15;
    }

    &:disabled {
      opacity: 0.5;
      cursor: wait;
    }
  }
`;

export const HeaderActionButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.25rem;
  height: 2.25rem;
  padding: 0;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 0.5rem;
  color: ${(p) => p.theme.colors.onPrimary};
  cursor: pointer;
  transition: all 0.2s ease;

  svg {
    width: 1.25rem;
    height: 1.25rem;
    transition: transform 0.2s ease;
  }

  &:hover {
    background: rgba(255, 255, 255, 0.2);
    border-color: rgba(255, 255, 255, 0.3);
    transform: scale(1.05);

    svg {
      transform: rotate(15deg);
    }
  }

  &:active {
    transform: scale(0.95);
  }

  &:disabled {
    cursor: wait;
    opacity: 0.5;
    transform: none;
  }

`;

export const OrdersPageWrapper = styled.div`
  /* Main Container */
  display: flex;
  flex-direction: column;
  height: calc(100vh - 3.5rem);
  @media (min-width: 768px) {
    height: calc(100vh - 5rem);
  }
  overflow: hidden;
  background: ${(p) => p.theme.colors.surface};

  /* General Utilities */
  .checkbox {
    width: 1rem;
    height: 1rem;
    margin: 0;
    cursor: pointer;
    accent-color: ${(p) => p.theme.colors.primary};
    transition: all 0.2s ease;

    &:hover {
      transform: scale(1.1);
    }
  }

  /* Sortable Item (Config) */
  .order-config-sortable-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.75rem 1rem;
    margin: 0.25rem 0;
    border-radius: 0.5rem;
    background: ${(p) => p.theme.colors.surface};
    border: 1px solid ${(p) => p.theme.colors.onSurface}15;
    transition: all 0.2s ease;

    &:hover {
      border-color: ${(p) => p.theme.colors.primary}50;
      ${cardShadow}
    }

    .order-config-sortable-actions {
      display: flex;
      gap: 0.5rem;

      .order-config-sortable-actions-item {
        display: flex;
        align-items: center;
        gap: 0.25rem;

        svg {
          cursor: pointer;
          opacity: 0.7;
          transition: all 0.2s ease;

          &:hover {
            opacity: 1;
            color: ${(p) => p.theme.colors.primary};
            transform: scale(1.1);
          }
        }
      }
    }
  }



  /* Table Structure */
  .table-wrapper {
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    position: relative;
  }

  .table-body {
    flex: 1;
    display: flex;
    overflow: auto;
    background: ${(p) => p.theme.colors.surface};
  }

  .orders-table-container {
    display: flex;
    width: 100%;
    min-height: 100%;
    background: ${(p) => p.theme.colors.surface};

    .orders-section {
      display: flex;
      flex-direction: column;
      min-width: 0;
      position: relative;
      border-right: 1px solid ${(p) => p.theme.colors.onSurface}10;

      &:last-child {
        border-right: none;
      }

      &.actions-section {
        flex-shrink: 0;
        min-width: fit-content;
        background: ${(p) => p.theme.colors.surface};
        z-index: 10;
        border-right: 1px solid ${(p) => p.theme.colors.onSurface}15;
      }

      &.fixed-section {
        flex-shrink: 0;
        min-width: fit-content;
        background: ${(p) => p.theme.colors.surface};
        z-index: 5;
        border-right: 1px solid ${(p) => p.theme.colors.onSurface}15;
      }

      &.scrollable-section {
        flex: 1;
        background: ${(p) => p.theme.colors.surface};
        width: 50%;
        min-width: inherit;
      }

      &.collapsed {
        max-width: 2.5rem;
        min-width: 2.5rem;
        overflow: hidden;
        border-right: 1px solid ${(p) => p.theme.colors.onSurface}15;

        .section-header {
          padding: 0 0.25rem;
          justify-content: center;

          .section-title {
            display: none;
          }
        }

        .section-content {
          display: none;
        }
      }

      .section-header {
        display: flex;
        align-items: center;
        justify-content: flex-start;
        gap: 0.5rem;
        height: 3rem;
        padding: 0 0.5rem;
        background: transparent;
        border-bottom: 1px solid ${(p) => p.theme.colors.onSurface}15;
        flex-shrink: 0;

        .section-toggle {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 1.5rem;
          height: 1.5rem;
          padding: 0;
          background: ${(p) => p.theme.colors.onSurface}10;
          border: 1px solid ${(p) => p.theme.colors.onSurface}20;
          border-radius: 0.25rem;
          color: ${(p) => p.theme.colors.onSurface};
          cursor: pointer;
          transition: all 0.2s ease;
          flex-shrink: 0;

          svg {
            width: 0.75rem;
            height: 0.75rem;
          }

          &:hover {
            background: ${(p) => p.theme.colors.primary}20;
            border-color: ${(p) => p.theme.colors.primary}40;
            color: ${(p) => p.theme.colors.primary};
          }
        }

        .section-title {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-weight: 800;
          font-size: 0.9rem;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          color: ${(p) => p.theme.colors.textLight};
          white-space: nowrap;

          .section-badge {
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 0.65rem;
            font-weight: 300;
            margin-bottom: -2px;
          }
        }
      }

      .section-content {
        flex: 1;
        display: flex;
        flex-direction: column;
        transition: opacity 0.3s ease;

        .section-columns-fixed {
          display: flex;
        }

        .section-columns {
          display: flex;
          overflow: scroll;
        }

        /* Column header directly in section-content (for actions section) */
        > .column-header {
          display: flex;
          align-items: center;
          height: 3rem;
          padding: 0 0.75rem;
          background: transparent;
          border-bottom: 1px solid ${(p) => p.theme.colors.onSurface}15;
          flex-shrink: 0;
          gap: 0.5rem;
        }
      }
    }
  }

  /* Columns & Rows */
  .column {
    display: flex;
    flex-direction: column;
    min-width: fit-content;
    flex-shrink: 0;

    .column-header {
      display: flex;
      align-items: center;
      height: 3rem;
      padding: 0 0.75rem;
      font-weight: 600;
      font-size: 0.9rem;
      color: ${(p) => p.theme.colors.text};
      background: transparent;
      border-bottom: 1px solid ${(p) => p.theme.colors.onSurface}15;
      white-space: nowrap;
      gap: 0.5rem;
      flex-shrink: 0;

      .select-all-checkbox {
        width: 1rem;
        height: 1rem;
        margin-right: 0.25rem;
        cursor: pointer;
        accent-color: ${(p) => p.theme.colors.primary};
      }
    }
  }

  .row {
    display: flex;
    align-items: center;
    min-height: 3.5rem;
    padding: 0.25rem 0.75rem;
    border-bottom: 1px solid ${(p) => p.theme.colors.onSurface}08;
    transition: all 0.15s ease;
    animation: ${fadeIn} 0.2s ease;
    background: transparent;

    &.odd {
      background: ${(p) => p.theme.colors.onSurface}03;
    }

    &.selected {
      border-left: 3px solid transparent;
      margin-left: -3px;

      &:hover {
         background: ${(p) => p.theme.colors.primary}20;
      }
    }

    &:not(.selected):hover {
      background: ${(p) => p.theme.colors.onSurface}08;
    }
  }

  .cell {
    display: flex;
    align-items: center;
    font-size: 0.95rem;
    color: ${(p) => p.theme.colors.text};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 15rem;
  }

  .action-cell {
    display: flex;
    align-items: center;
    gap: 0.25rem;
  }

  .action-button {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 2.5rem;
    height: 2.5rem;
    padding: 0;
    background: transparent;
    border: none;
    border-radius: 0.375rem;
    color: ${(p) => p.theme.colors.onBackground}CC;
    cursor: pointer;
    transition: all 0.2s ease;

    .icon {
      color: inherit;
      fill: currentColor;
    }

    svg {
      width: 1.4rem;
      height: 1.4rem;
    }

    &:hover {
      opacity: 1;
      background: ${(p) => p.theme.colors.onSurface}10;
      transform: scale(1.1);
    }

    &:active {
      transform: scale(0.95);
    }

    &.positive {
       color: ${(p) => p.theme.colors.positive};
       &:hover { background: ${(p) => p.theme.colors.positive}15; }
    }

    &.warning {
       color: ${(p) => p.theme.colors.warning};
       &:hover { background: ${(p) => p.theme.colors.warning}15; }
    }

    &.negative {
       color: ${(p) => p.theme.colors.negative};
       &:hover { background: ${(p) => p.theme.colors.negative}15; }
    }
  }

  /* Navigation / Pagination */
  .navigation-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 1rem;
    padding: 0.75rem 1rem;
    background: ${(p) => p.theme.colors.surface};
    border-top: 1px solid ${(p) => p.theme.colors.onSurface}10;
    ${glassMorphism}

    .pagination-wrapper {
      display: flex;
      align-items: center;
      gap: 0.5rem;
    }

    .per-page-wrapper {
        display: flex;
        align-items: center;
        gap: 0.5rem;

        .per-page-label {
            font-size: 0.85rem;
            color: ${(p) => p.theme.colors.text};
            font-weight: 500;
        }
    }
  }

  .page-button {
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 2rem;
    height: 2rem;
    padding: 0 0.5rem;
    background: ${(p) => p.theme.colors.surface};
    color: ${(p) => p.theme.colors.text};
    border: 1px solid ${(p) => p.theme.colors.onSurface}20;
    border-radius: 0.5rem;
    font-weight: 500;
    font-size: 0.85rem;
    cursor: pointer;
    transition: all 0.2s ease;
    ${cardShadow}

    &.active {
      background: ${(p) => p.theme.colors.primary};
      color: ${(p) => p.theme.colors.onPrimary};
      border-color: ${(p) => p.theme.colors.primary};
    }

    &.disabled {
      cursor: not-allowed;
      opacity: 0.4;
      ${cardShadow}
    }

    &:not(.disabled):not(.active):hover {
      border-color: ${(p) => p.theme.colors.primary}60;
      background: ${(p) => p.theme.colors.primary}10;
      transform: translateY(-1px);
      ${cardShadowHover}
    }

    &:active:not(.disabled) {
      transform: translateY(0);
    }

    svg {
      width: 1rem;
      height: 1rem;
    }
  }

  .page-input {
    width: 3rem;
    height: 2rem;
    padding: 0 0.5rem;
    text-align: center;
    background: ${(p) => p.theme.colors.surface};
    color: ${(p) => p.theme.colors.text};
    border: 1px solid ${(p) => p.theme.colors.onSurface}20;
    border-radius: 0.5rem;
    font-weight: 500;
    font-size: 0.85rem;
    outline: none;
    transition: all 0.2s ease;
    ${cardShadow}

    &:focus {
      border-color: ${(p) => p.theme.colors.primary};
      box-shadow: 0 0 0 2px ${(p) => p.theme.colors.primary}20;
    }

    /* Hide spinners */
    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }
  }

  .page-info {
    font-size: 0.85rem;
    color: ${(p) => p.theme.colors.textLight};
    white-space: nowrap;
  }

  /* Status Badge */
  .status-badge {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    padding: 0.125rem 0.5rem;
    font-size: 0.7rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    border-radius: 1rem;
    white-space: nowrap;
    background: ${(p) => p.theme.colors.onSurface}15;
    color: ${(p) => p.theme.colors.text};

    &.success {
      background: ${(p) => p.theme.colors.positive}20;
      color: ${(p) => p.theme.colors.positive};
    }
    &.error {
      background: ${(p) => p.theme.colors.negative}20;
      color: ${(p) => p.theme.colors.negative};
    }
    &.warning {
      background: ${(p) => p.theme.colors.warning}20;
      color: ${(p) => p.theme.colors.warning};
    }
  }

  /* Empty State */
  .empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;
    min-height: 300px;
    gap: 1rem;
    color: ${(p) => p.theme.colors.textLight};

    svg {
      width: 4rem;
      height: 4rem;
      opacity: 0.5;
    }

    h3 {
      font-size: 1.25rem;
      font-weight: 600;
      color: ${(p) => p.theme.colors.text};
    }

    p {
      font-size: 0.9rem;
      text-align: center;
      max-width: 300px;
    }
  }

  /* Skeleton */
  .skeleton-cell {
    height: 1rem;
    width: 100%;
    max-width: 8rem;
    background: linear-gradient(
      90deg,
      ${(p) => p.theme.colors.onSurface}10 0%,
      ${(p) => p.theme.colors.onSurface}20 50%,
      ${(p) => p.theme.colors.onSurface}10 100%
    );
    background-size: 200% 100%;
    animation: ${shimmer} 1.5s infinite;
    border-radius: 0.25rem;
  }
`;
